import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  UsersIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { useUser } from '../context/UserContext';
import {
  getCompanyDetails,
  getCompanyMembers,
  getCompanySubscription
} from '../utils/companyClient';

// Tab Components
import TeamManagement from '../components/company/TeamManagement';
import SubscriptionSettings from '../components/company/SubscriptionSettings';
import CompanySettingsTab from '../components/company/CompanySettingsTab';
import GlobalPayTables from './settings/GlobalPayTables';
import NoCompanyState from '../components/company/NoCompanyState';

export default function CompanySettings() {
  const { currentUser, currentCompany } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'team';

  // State management
  const [companyData, setCompanyData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // User permissions
  const userMembership = useMemo(() => {
    if (!currentUser || !teamMembers.length) return null;
    return teamMembers.find(member => member.user_id === currentUser?.id);
  }, [teamMembers, currentUser]);

  const canManageTeam = useMemo(() => {
    const role = userMembership?.role;
    return role === 'owner' || role === 'admin';
  }, [userMembership]);

  const canManageSubscription = useMemo(() => {
    const role = userMembership?.role;
    return role === 'owner' || role === 'admin';
  }, [userMembership]);

  const canManageSettings = useMemo(() => {
    const role = userMembership?.role;
    return role === 'owner' || role === 'admin';
  }, [userMembership]);

  // Tab configuration with permissions
  const tabs = useMemo(() => [
    {
      id: 'team',
      name: 'Team',
      icon: UsersIcon,
      description: 'Manage team members and invitations',
      enabled: true,
      badge: teamMembers.length > 0 ? teamMembers.length : null
    },
    {
      id: 'settings',
      name: 'Details',
      icon: Cog6ToothIcon,
      description: 'Company configuration and preferences',
      enabled: canManageSettings,
      restricted: !canManageSettings
    },
    {
      id: 'subscription',
      name: 'Subscription',
      icon: CreditCardIcon,
      description: 'Subscription status and access',
      enabled: canManageSubscription,
      restricted: !canManageSubscription
    },
    
  ], [teamMembers.length, canManageSubscription, canManageSettings]);
  
  // Get the full info for the currently active tab
  const activeTabInfo = useMemo(() => {
    return tabs.find(tab => tab.id === activeTab) || tabs[0];
  }, [tabs, activeTab]);


  // Load company data on mount
  useEffect(() => {
    if (currentCompany?.id) {
      loadCompanyData();
    } else {
      setLoading(false);
      setCompanyData(null);
      setTeamMembers([]);
      setSubscriptionData(null);
    }
  }, [currentCompany?.id]);

  const loadCompanyData = async () => {
  if (!currentCompany?.id) return;

  try {
    setLoading(true);
    setError('');

    const [company, members, subscription] = await Promise.all([
      getCompanyDetails(currentCompany.id),
      getCompanyMembers(currentCompany.id),
      getCompanySubscription(currentCompany.id) // Always fetch, let component handle permissions
    ]);

    setCompanyData(company);
    setTeamMembers(members);
    setSubscriptionData(subscription);

  } catch (err) {
    console.error('Error loading company data:', err);
    setError('Failed to load company information');
  } finally {
    setLoading(false);
  }
};

  // Handle tab changes
  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  // Refresh data after changes
  const refreshData = () => {
    loadCompanyData();
  };

  if (loading && !companyData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading company data...</p>
        </div>
      </div>
    );
  }

  if (!currentCompany) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <NoCompanyState />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Fixed Header Section */}
      <div className="flex-shrink-0">
        {/* Header */}
        <div className="h-14 bg-white border-b border-gray-200 px-6 flex items-center">
          <h1 className="text-lg font-semibold text-gray-900">Settings</h1>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b border-gray-200">
          <div className="mx-auto px-4 sm:px-6 lg:px-8">
            {/* Flex container to align tabs and invite code */}
            <div className="flex justify-between items-center">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  const isEnabled = tab.enabled && !tab.restricted;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => isEnabled && handleTabChange(tab.id)}
                    disabled={!isEnabled}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      isActive
                        ? 'border-red-500 text-red-600'
                        : isEnabled
                        ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        : 'border-transparent text-gray-300 cursor-not-allowed'
                    } transition-colors`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.name}
                    
                    {tab.badge && (
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {tab.badge}
                      </span>
                    )}
                    
                    {tab.restricted && (
                      <span className="ml-1 text-xs">(Restricted)</span>
                    )}
                  </button>
                  );
                })}
              </nav>

              {/* Invite Code display */}
              {companyData?.invite_code && (
                <div className="hidden md:flex items-center text-sm flex-shrink-0 border border-gray-200 rounded bg-gray-100 px-2 py-1">
                  <span className="font-medium text-gray-600">
                    Invite Code:
                  </span>
                  <code className="ml-1 p-1 bg-gray-100 text-gray-800 rounded text-xs font-mono">
                    {companyData.invite_code}
                  </code>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-auto">
        <div className="mx-auto">
          {error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="text-red-800">
                <h3 className="font-medium">Error Loading Company Data</h3>
                <p className="mt-1">{error}</p>
                <button
                  onClick={loadCompanyData}
                  className="mt-3 text-sm bg-red-100 hover:bg-red-200 px-3 py-1 rounded"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Tab Content */}
              {activeTab === 'team' && (
                <TeamManagement
                  company={companyData}
                  teamMembers={teamMembers}
                  currentUser={currentUser}
                  userRole={userMembership?.role}
                  canManage={canManageTeam}
                  onRefresh={refreshData}
                />
              )}

              {activeTab === 'subscription' && canManageSubscription && (
                <SubscriptionSettings
                  company={companyData}
                  subscription={subscriptionData}
                  userRole={userMembership?.role}
                  onRefresh={refreshData}
                />
              )}

              {activeTab === 'settings' && canManageSettings && (
                <CompanySettingsTab
                  company={companyData}
                  currentUser={currentUser}
                  userRole={userMembership?.role}
                  onRefresh={refreshData}
                />
              )}

              {activeTab === 'paytables' && canManageSettings && (
                <GlobalPayTables />
              )}

              {/* Access Denied */}
              {((activeTab === 'subscription' && !canManageSubscription) ||
                (activeTab === 'settings' && !canManageSettings) ||
                (activeTab === 'paytables' && !canManageSettings)) && (
                <div className="text-center py-12">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
                    <div className="text-yellow-800">
                      <h3 className="font-medium">Access Restricted</h3>
                      <p className="mt-1">
                        You don't have permission to access this section. 
                        Contact a company owner for access.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
